import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { parameterizePath, trackView } from '@/lib/countly';

/**
 * Subscribes to React Router location changes and reports each
 * navigation as a Countly page view. Mount exactly once inside the
 * router tree (PageLayout is a good home — it wraps every authed page).
 *
 * Strict-Mode safe: a ref dedupes the duplicate effect fire React 18
 * does in development, so a single navigation doesn't get logged twice.
 *
 * Path is parameterized via `parameterizePath` so numeric segments
 * (patient ids, plan ids) collapse to `:id` before being sent — keeps
 * PII out of Countly view names and keeps the dashboard's view list
 * small enough to read.
 */
export const useCountlyViews = (): void => {
  const { pathname } = useLocation();
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;
    trackView(parameterizePath(pathname));
  }, [pathname]);
};
